import React, { useState, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import {
  CURSOR_DARK_THEME,
  LANG_STYLE,
  detectLang,
  registerEditorAssets,
  clearEditorMarkers,
  formatPhpCode,
  EDITOR_BASE_OPTIONS,
} from './codeEditorShared';

const FlowChartCodeEditorPanel = ({
  /* Layout */
  showCodeEditor,
  codePanelWidth,
  isResizing,
  setIsResizing,
  resizeStartXRef,
  resizeStartWidthRef,
  setShowCodeEditor,
  /* Editor içeriği */
  selectedShape,
  selectedShapeLabel,
  code,
  handleCodeChange,
  handleEditorDidMount,
  aiStatus,
  pendingChange,
  onApplyPendingChange,
  onDiscardPendingChange,
  isApplyingChange,
  currentPrompt,
  onPromptChange,
  onRunShapeAI,
  isRunningShapeAI,
  /* ── Çoklu tab desteği (opsiyonel) ── */
  editorTabs,       // [{ shapeId, shapeLabel }]
  onTabActivate,    // (shapeId) => void
  onTabClose,       // (shapeId) => void
  onTabDetach,      // (shapeId, shapeLabel) => void
}) => {
  const [showPromptPanel, setShowPromptPanel] = useState(true);
  const [promptPanelHeight, setPromptPanelHeight] = useState(30);
  const [isResizingPrompt, setIsResizingPrompt] = useState(false);
  const resizeStartYRef = useRef(0);
  const resizeStartHeightRef = useRef(0);
  const containerHeightRef = useRef(0);
  const editorRef = useRef(null);

  const lang = detectLang(selectedShapeLabel);
  const langStyle = LANG_STYLE[lang] || LANG_STYLE.php;
  const langLabel = lang.toUpperCase()
    .replace('JAVASCRIPT', 'JS')
    .replace('TYPESCRIPT', 'TS');

  const handleBeforeMount = useCallback((monaco) => {
    registerEditorAssets(monaco);
  }, []);

  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    clearEditorMarkers(editor, monaco);
    editor.addCommand(
      monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
      () => editor.getAction('editor.action.formatDocument')?.run(),
    );
    if (typeof handleEditorDidMount === 'function') {
      handleEditorDidMount(editor, monaco);
    }
  }, [handleEditorDidMount]);

  const handleFormat = useCallback(() => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  }, []);

  const startPromptResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const panel = e.currentTarget.parentElement;
    const rect = panel.getBoundingClientRect();
    containerHeightRef.current = rect.height;
    resizeStartYRef.current = e.clientY;
    resizeStartHeightRef.current = promptPanelHeight;
    setIsResizingPrompt(true);
    const onMove = (ev) => {
      const pct = ((resizeStartYRef.current - ev.clientY) / (containerHeightRef.current || 600)) * 100;
      setPromptPanelHeight((h) => Math.min(60, Math.max(15, resizeStartHeightRef.current + pct)));
    };
    const onUp = () => {
      setIsResizingPrompt(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [promptPanelHeight]);

  if (!showCodeEditor) return null;

  const multiTab = Array.isArray(editorTabs) && editorTabs.length > 0;

  return (
    <>
      {/* ── Horizontal Resize Handle ── */}
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          resizeStartXRef.current = e.clientX;
          resizeStartWidthRef.current = codePanelWidth;
          setIsResizing(true);
        }}
        style={{
          width: 5, background: isResizing ? '#388bfd' : '#21262d',
          cursor: 'col-resize', flexShrink: 0,
          position: 'relative', zIndex: 10000, userSelect: 'none',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => { if (!isResizing) e.currentTarget.style.background = '#388bfd'; }}
        onMouseLeave={(e) => { if (!isResizing) e.currentTarget.style.background = '#21262d'; }}
      />

      {/* ── Panel ── */}
      <div style={{
        flex: `0 0 ${codePanelWidth}%`, minWidth: 300, maxWidth: '75%',
        width: `${codePanelWidth}%`, background: '#0d1117',
        borderLeft: '1px solid #21262d', display: 'flex',
        flexDirection: 'column', overflow: 'hidden',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>

        {/* ══ SATIR 1: Tab Bar — tam genişlik, toolbar YOK ══ */}
        <div style={{
          background: '#010409',
          borderBottom: '1px solid #21262d',
          display: 'flex',
          alignItems: 'stretch',
          height: 34,
          overflow: 'hidden',
        }}>
          {multiTab ? (
            /* Çoklu sekmeler — tam genişlik */
            <div style={{
              display: 'flex',
              flex: 1,
              overflowX: 'auto',
              alignItems: 'stretch',
              scrollbarWidth: 'thin',
              scrollbarColor: '#30363d #010409',
              minWidth: 0,
            }}>
              {editorTabs.map((tab) => {
                const isActive = tab.shapeId === selectedShape;
                const tLang = detectLang(tab.shapeLabel);
                const tStyle = LANG_STYLE[tLang] || LANG_STYLE.php;
                const label = tab.shapeLabel || tab.shapeId || '';
                const shortLabel = label.includes('.') ? label.split('/').pop() : label;
                return (
                  <div
                    key={tab.shapeId}
                    onClick={() => onTabActivate?.(tab.shapeId)}
                    title={label}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '0 5px 0 11px',
                      cursor: 'pointer', flexShrink: 0,
                      borderRight: '1px solid #21262d',
                      borderBottom: isActive ? `2px solid ${tStyle.color}` : '2px solid transparent',
                      background: isActive ? '#0d1117' : 'transparent',
                      width: 160, minWidth: 90,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#161b22'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: tStyle.color, flexShrink: 0,
                    }} />
                    <span style={{
                      color: isActive ? '#e6edf3' : '#7d8590',
                      fontSize: 11.5, fontWeight: isActive ? 500 : 400,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      flex: 1, minWidth: 0,
                    }}>
                      {shortLabel}
                    </span>
                    {/* Detach: hover'da belirir */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onTabDetach?.(tab.shapeId, tab.shapeLabel); }}
                      title="Yüzer panele çıkar"
                      style={{
                        background: 'none', border: 'none', color: '#484f58',
                        cursor: 'pointer', padding: '2px', lineHeight: 1,
                        fontSize: 10, flexShrink: 0, opacity: isActive ? 0.4 : 0,
                        transition: 'opacity 0.15s, color 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#79c0ff'; e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; e.currentTarget.style.opacity = isActive ? '0.4' : '0'; }}
                    >↗</button>
                    {/* Close */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onTabClose?.(tab.shapeId); }}
                      title="Kapat"
                      style={{
                        background: 'none', border: 'none', color: '#484f58',
                        cursor: 'pointer', padding: '2px 3px', lineHeight: 1,
                        fontSize: 13, flexShrink: 0,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#e6edf3'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
                    >×</button>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Tekli sekme */
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7, flex: 1,
              padding: '0 14px',
              borderBottom: `2px solid ${langStyle.color}`,
              background: '#0d1117', cursor: 'default',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24" rx="4" fill={langStyle.bg} />
                <text x="12" y="17" textAnchor="middle" fontSize="10" fill={langStyle.color} fontWeight="700">
                  {langLabel.slice(0, 2)}
                </text>
              </svg>
              <span style={{
                color: '#cdd9e5', fontSize: 12, fontWeight: 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                flex: 1, minWidth: 0,
              }}>
                {selectedShape ? (selectedShapeLabel || selectedShape) : 'Script Editor'}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                color: langStyle.color, background: langStyle.bg,
                border: `1px solid ${langStyle.border}`, borderRadius: 4,
                padding: '2px 6px', flexShrink: 0,
              }}>
                {langLabel}
              </span>
            </div>
          )}
        </div>

        {/* ══ SATIR 2: Toolbar — Format · Agent · Kapat ══ */}
        <div style={{
          background: '#010409',
          borderBottom: '1px solid #21262d',
          display: 'flex', alignItems: 'center',
          padding: '0 8px', height: 30, gap: 5,
        }}>
          {pendingChange ? (
            <>
              <button onClick={onDiscardPendingChange} disabled={isApplyingChange} style={{
                background: 'transparent', border: '1px solid #30363d', color: '#8b949e',
                padding: '2px 9px', borderRadius: 4, fontSize: 11, flexShrink: 0,
                cursor: isApplyingChange ? 'not-allowed' : 'pointer', fontWeight: 500,
              }}>Undo</button>
              <button onClick={onApplyPendingChange} disabled={isApplyingChange} style={{
                background: isApplyingChange ? '#161b22' : 'linear-gradient(135deg,#1a7f37,#238636)',
                border: '1px solid #2ea04330', color: '#fff', flexShrink: 0,
                padding: '2px 9px', borderRadius: 4, fontSize: 11,
                cursor: isApplyingChange ? 'not-allowed' : 'pointer', fontWeight: 600,
              }}>Keep</button>
            </>
          ) : (
            selectedShape && (
              <button onClick={handleFormat} title="Kodu Formatla (Shift+Alt+F)" style={{
                background: 'transparent', border: '1px solid #21262d', color: '#8b949e',
                padding: '2px 7px', borderRadius: 4, fontSize: 11, cursor: 'pointer', flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.15s',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#388bfd'; e.currentTarget.style.color = '#79c0ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#21262d'; e.currentTarget.style.color = '#8b949e'; }}
              >
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <line x1="2" y1="4" x2="14" y2="4" />
                  <line x1="2" y1="8" x2="10" y2="8" />
                  <line x1="2" y1="12" x2="12" y2="12" />
                  <polyline points="12 6 14 8 12 10" />
                </svg>
                Format
              </button>
            )
          )}

          <button onClick={() => setShowPromptPanel(!showPromptPanel)} style={{
            background: showPromptPanel ? 'linear-gradient(135deg,#3730a3,#6d28d9)' : 'transparent',
            border: `1px solid ${showPromptPanel ? '#6d28d980' : '#30363d'}`,
            color: showPromptPanel ? '#fff' : '#8b949e',
            padding: '2px 7px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
            fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.15s',
            flexShrink: 0,
          }} title="AI Agent Paneli">
            <span style={{ fontSize: 11 }}>✦</span>
            Agent
          </button>

          <div style={{ flex: 1 }} />

          <button onClick={() => setShowCodeEditor(false)} style={{
            background: 'transparent', border: 'none', color: '#484f58',
            padding: '3px 5px', cursor: 'pointer', fontSize: 16, lineHeight: 1,
            borderRadius: 4, transition: 'color 0.1s', flexShrink: 0,
          }} title="Kapat"
            onMouseEnter={(e) => { e.currentTarget.style.color = '#e6edf3'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
          >×</button>
        </div>

        {/* ── Monaco Editor ── */}
        <div style={{
          flex: showPromptPanel ? `1 1 ${100 - promptPanelHeight}%` : '1',
          minHeight: 200, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          {selectedShape ? (
            <Editor
              height="100%" width="100%"
              language={lang} value={code}
              theme="cursor-dark"
              onChange={handleCodeChange}
              onMount={handleEditorMount}
              beforeMount={handleBeforeMount}
              key={`editor-${selectedShape}`}
              options={{ ...EDITOR_BASE_OPTIONS, readOnly: Boolean(pendingChange) }}
            />
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', gap: 12, color: '#484f58',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Bir şekil seçin</span>
            </div>
          )}
        </div>

        {/* ── Vertical Resize (prompt panel) ── */}
        {showPromptPanel && (
          <div onMouseDown={startPromptResize} style={{
            height: 5, background: isResizingPrompt ? '#388bfd' : '#21262d',
            cursor: 'row-resize', flexShrink: 0, transition: 'background 0.15s',
          }}
            onMouseEnter={(e) => { if (!isResizingPrompt) e.currentTarget.style.background = '#388bfd'; }}
            onMouseLeave={(e) => { if (!isResizingPrompt) e.currentTarget.style.background = '#21262d'; }}
          />
        )}

        {/* ── AI Prompt Panel ── */}
        {showPromptPanel && (
          <div style={{
            flex: `0 0 ${promptPanelHeight}%`, minHeight: 120, maxHeight: '60%',
            display: 'flex', flexDirection: 'column',
            background: '#0d1117', borderTop: '1px solid #21262d',
          }}>
            <div style={{
              background: '#010409', padding: '5px 10px',
              borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 12, color: '#a78bfa' }}>✦</span>
              <span style={{ color: '#cdd9e5', fontSize: 12, fontWeight: 600 }}>AI Prompt</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => onRunShapeAI?.(currentPrompt)}
                disabled={!currentPrompt?.trim() || isRunningShapeAI}
                title="Prompt'u AI'a gönder ve kod üret"
                style={{
                  background: (!currentPrompt?.trim() || isRunningShapeAI)
                    ? 'transparent'
                    : 'linear-gradient(135deg,#3730a3,#6d28d9)',
                  border: `1px solid ${(!currentPrompt?.trim() || isRunningShapeAI) ? '#30363d' : '#7c3aed80'}`,
                  color: (!currentPrompt?.trim() || isRunningShapeAI) ? '#484f58' : '#e2d9f3',
                  padding: '3px 10px', borderRadius: 4, fontSize: 11, cursor: (!currentPrompt?.trim() || isRunningShapeAI) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600,
                  transition: 'all 0.15s', flexShrink: 0,
                }}
              >
                {isRunningShapeAI ? (
                  <>
                    <div style={{ width: 10, height: 10, border: '1.5px solid #484f58', borderTopColor: '#a78bfa', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span>Üretiliyor…</span>
                  </>
                ) : (
                  <>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M4 4l16 8-16 8V4z" strokeLinejoin="round" />
                    </svg>
                    <span>Derle</span>
                  </>
                )}
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: '10px 12px' }}>
              <textarea
                value={currentPrompt || ''}
                onChange={(e) => onPromptChange(e.target.value)}
                placeholder="AI talimatınızı buraya yazın..."
                style={{
                  width: '100%', height: '100%', background: '#161b22',
                  border: '1px solid #21262d', borderRadius: 6, color: '#e6edf3',
                  fontSize: 12.5,
                  fontFamily: "'Cascadia Code','Fira Code','JetBrains Mono',Consolas,'Courier New',monospace",
                  lineHeight: 1.6, padding: '10px 12px', resize: 'none',
                  outline: 'none', boxSizing: 'border-box', caretColor: '#79c0ff',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#388bfd'; }}
                onBlur={(e) => { e.target.style.borderColor = '#21262d'; }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FlowChartCodeEditorPanel;
